import React, { useEffect, useState } from 'react';
import { AuthenticationStatus } from './enums/authentication-status';
import { FetchType } from './enums';
import { Loader } from './components/general/loading-state/loaders/loader/loader';
import { KeplerState } from './models/kepler-state';
import { Outlet } from 'react-router';
import { page, track } from './library/helpers/segment';
import { AppLoaderLayer } from './components/general/loading-state/loader-layers/app-loader-layer/app-loader-layer';
import { useAppActions, useAppState } from './overmind';
import { useWatchLocation } from './library/hooks/useWatchLocation';
import { useDataSyncTimeout } from './library/hooks/useDataSyncTimeout';
import { NavigationOutlet } from './navigation/navigation.outlet';
import { Mode, PagePath } from './navigation/navigation.enums';
import { useMatchScreenWidth, useRefocus } from '@keplerco/core';
import { useWatchSearchParams } from './library/hooks/useWatchSearchParams';
import { scrollToTop } from './library/helpers/scroll-to-top';
import { createPortal } from 'react-dom';
import { Notifications } from './notifications/notifications';

export function App(): JSX.Element {
  const actions = useAppActions();
  const { authenticationStatus, isInitialising, user, mayViewSideNav, sideNavVisible, sideNavCollapsed, companyVariables, mode } = useAppState<KeplerState>();

  const isTablet = useMatchScreenWidth('tablet');
  const isMobile = useMatchScreenWidth('mobile');

  const [mainPaddingLeft, setMainPaddingLeft] = useState<number>(0);

  useWatchLocation(() => {
    page();
    scrollToTop();
  });

  useWatchSearchParams(() => scrollToTop());

  useEffect(() => {
    function watchClicks(event: MouseEvent) {
      const target = event.target as HTMLElement;

      const analyticsProperties: IAnalyticsProperties = {
        userId: user?.learnerSlug,
        companyId: user?.companySlug,

        elementTag: target.tagName.toLowerCase(),
        elementId: target.id,
        elementContent: target.textContent,
      };

      track('KPLR-CLICK-EVENT', analyticsProperties);
    }

    function watchSubmit(event: SubmitEvent) {
      const target = event.target as HTMLFormElement;

      const analyticsProperties: IAnalyticsProperties = { formId: target.id };
      [...new FormData(target).entries()].forEach(entry => {
        if (!entry[0].toLowerCase().includes('password')) analyticsProperties[entry[0]] = entry[1];
      });

      track('KPLR-FORM-EVENT', analyticsProperties);
    }

    window.addEventListener('click', watchClicks);
    window.addEventListener('submit', watchSubmit);

    return () => {
      window.removeEventListener('click', watchClicks);
      window.removeEventListener('submit', watchSubmit);
    };
  }, [user]);

  useEffect(() => {
    async function authenticate() {
      await Promise.all([
        actions.authenticate(),
        actions.setSamlUrl(),
        actions.setRegistrationType()
      ]);
    }

    async function initialise() {
      actions.startLoader({ path: PagePath.root, type: FetchType.PageFetching });
      await actions.initialiseForeground();
      actions.stopLoader(PagePath.root);

      await actions.initialiseBackground();
    }

    if (authenticationStatus === AuthenticationStatus.Unknown) authenticate();
    if (authenticationStatus === AuthenticationStatus.Authenticated) initialise();
  }, [authenticationStatus]);

  useEffect(() => {
    if (!companyVariables.slug) return;
    mode === Mode.AdminDashboard && actions.getCompanyVariables(companyVariables.slug);
    actions.getSkillLevelConfig();
  }, [companyVariables.slug]);

  useEffect(() => {
    if (isMobile || isTablet) return void setMainPaddingLeft(0);
    if (!mayViewSideNav) return void setMainPaddingLeft(0);
    if (!sideNavVisible) return void setMainPaddingLeft(0);
    if (sideNavCollapsed) return void setMainPaddingLeft(90);
    setMainPaddingLeft(245);
  }, [mayViewSideNav, sideNavVisible, sideNavCollapsed, isTablet, isMobile]);

  useDataSyncTimeout();
  useRefocus(actions.syncCourseContent);

  if (isInitialising || authenticationStatus === AuthenticationStatus.Unknown)
    return (
      <div style={{ width: '100vw', height: '100vh' }}>
        <Loader />
      </div>
    );

  return (
    <main style={{ paddingLeft: mainPaddingLeft }}>
      <NavigationOutlet isMobile={isMobile} isTablet={isTablet} />
      <Outlet />
      <AppLoaderLayer />
      {createPortal(<Notifications />, window.document.body)}
    </main>
  );
}
